import { useEffect, useState } from 'react';
import { visualState, type DesktopState } from '../state';
import { CallExtras } from './CallExtras';

const TEXT: Record<string, string> = { paused: 'PAUSED', 'locating-contact': 'LOCATING CONTACT', 'live-call': 'LIVE CALL', 'hung-up': 'HUNG UP', dead: 'CUSTOMER GONE' };

export function fmt(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

export function useTicker(intervalMs = 1000): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), intervalMs); return () => clearInterval(t); }, [intervalMs]);
  return now;
}

export function LiveStatusBar({ s, onVerb, onTransfer, onDtmf, onRecord, onDropThirdParty, onLeaveThreeWay }: {
  s: DesktopState; onVerb: (v: 'hold' | 'resume' | 'mute' | 'unmute' | 'hangup') => void; onTransfer: () => void; onDtmf: (digit: string) => void; onRecord: () => void;
  onDropThirdParty?: (memberId: string) => void; onLeaveThreeWay?: () => void;
}) {
  const now = useTicker();
  const vs = visualState(s);
  const call = s.call;
  const timerLabel = vs === 'live-call' ? 'CALL TIME' : vs === 'paused' ? 'TIME PAUSED' : vs === 'locating-contact' ? 'WAITING' : 'SINCE HANGUP';
  const since = vs === 'live-call' && call ? (call.bridged_at ?? call.offered_at) : s.stateSince;
  const lead = call?.lead;
  const name = lead ? [lead.data['first_name'], lead.data['last_name']].filter(Boolean).join(' ') : '';
  const live = s.phase === 'live' || s.phase === 'incoming';
  const third = s.room?.third_party?.[0];
  return (
    <div id="x5-live-status-bar" className={vs}>
      <div id="x5-live-status-color">
        <span id="x5-live-status-text">{TEXT[vs]}</span>
        <span id="x5-live-status-call-time-text">{timerLabel} {fmt(now - since)}</span>
      </div>
      <div id="x5-live-status-customer">
        {call ? (
          <>
            <span id="x5-live-status-customer-name">{name || call.customer?.cid_name || 'Customer'}</span>
            <span> · {lead?.phone_number ?? call.customer?.cid_num ?? ''}</span>
            <div id="x5-live-status-detail">Call ID {call.outbound_call_id} · Lead {lead?.lead_id ?? '—'} · List {lead?.list_id ?? '—'}{call.held ? ' · ON HOLD' : ''}{call.muted ? ' · MUTED' : ''}{call.recording ? ' · REC' : ''}{third ? ` · 3-WAY with ${third.cid_num || third.cid_name || 'third party'}` : ''}</div>
          </>
        ) : (
          <span id="x5-live-status-customer-name" style={{ color: 'var(--muted)' }}>{s.agentState === 'ready' ? 'Waiting for the next contact…' : 'No active call'}</span>
        )}
      </div>
      <div id="x5-live-status-action-buttons">
        {third && onDropThirdParty && <button id="x5-button-drop-3way" onClick={() => onDropThirdParty(third.member_id)}>Drop 3rd party</button>}
        {third && onLeaveThreeWay && <button id="x5-button-leave-3way" onClick={onLeaveThreeWay} title="You leave; the customer stays connected to the third party">Leave 3-way</button>}
        <button id="x5-button-transfer-call" onClick={onTransfer} disabled={!live}>Transfer</button>
        <button onClick={() => onVerb(call?.held ? 'resume' : 'hold')} disabled={!live}>{call?.held ? 'Resume' : 'Hold'}</button>
        <button onClick={() => onVerb(call?.muted ? 'unmute' : 'mute')} disabled={!live}>{call?.muted ? 'Unmute' : 'Mute'}</button>
        <CallExtras live={live} recording={!!call?.recording} mode={s.config?.campaign_recording} onDtmf={onDtmf} onRecord={onRecord} />
        <button className="danger" onClick={() => onVerb('hangup')} disabled={!live}>Hangup</button>
      </div>
    </div>
  );
}
